import React, { useState, useEffect } from 'react';
import { apiUrl } from '../../ApiHelper/ApiUrl';
import {
    MoreVertical, Eye, Download, Mail, Plus,
    FileText, Calendar, FileBarChart, Loader2, Blocks
} from 'lucide-react';
import FilePreviewModal from '../../components/FilePreviewModal';
import ReportsItem from './ReportsItem';
import { handleFetchRequest } from '../../ApiHelper/ApiHelper';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectReports, makeReport } from '../../redux/features/reports/reportSlice';
import { Spin, Layout } from 'antd';
const { Content } = Layout;



// --- MAIN REPORTS VIEW ---
const ReportsView = () => {
    const [pageIsLoading, setPageIsLoading] = useState(false);
    const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
    const [reports, setReports] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false); // Local loader state specifically for the button    
    const [generatedReports, setGeneratedReports] = useState([]);

    const [project, setProject] = useState(null);    
    const [projectId, setProjectId] = useState(localStorage.getItem("selectedProject"));

    const dispatch = useDispatch();
    const { switched } = useSelector((state => state?.rootReducer?.project || {}));
    const { currentUser } = useSelector((state => state?.rootReducer?.users || {}));

    useEffect(() => {
        if(!switched?.projectId) return;
        setProjectId(switched?.projectId);  
        setProject(switched);
    }, [switched]);

    const generateReport = async () => {
        setPageIsLoading(true);
        setIsGenerating(true);

        try {
            const result = await dispatch(makeReport(projectId)).unwrap();
            if(result?.status) {
                getListOfReports();
                setGeneratedReports(result?.data);
                setIsEvidenceModalOpen(true);
            }
        } catch (error) {
            console.log('##error', error);
        } finally {
            setPageIsLoading(false);
            setIsGenerating(false);
        }
        // handleFetchRequest("report", `${apiUrl.project_report}/${projectId}`, "POST").then((response) => {
        //     if (response.statusCode === 200) {
        //         getListOfReports();
        //         setGeneratedReports(response?.data);
        //         setIsEvidenceModalOpen(true);
        //     } else {
        //         setIsEvidenceModalOpen(true);
        //         setGeneratedReports(response?.data);
        //     }
        //     setPageIsLoading(false);
        //     setIsGenerating(false);
        // });
    }

    const getListOfReports = async () => {
        setPageIsLoading(true);
        try {
            const result = await dispatch(getProjectReports(projectId)).unwrap();
            if(result?.status) {
                setReports(result?.data || []);
            }            
        } catch (error) {
            console.log('##error', error);
        } finally {
            setPageIsLoading(false);
        }
    }

    const manageViewButton = (open, report) => {
        setIsEvidenceModalOpen(open);
        setGeneratedReports(report);
    }

    useEffect(() => {
        getListOfReports();
    }, [project]);

    return (
        
        <Content className="main-body" id="scrolly-div">
            <Spin spinning={pageIsLoading} size="large">

                <div className="animate-in fade-in zoom-in-[0.98] duration-400 ease-out  mx-auto">

                    <div className=" bg-slate-50  relative">

                        {/* --- HEADER --- */}
                        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-600/20 text-white">
                                    <FileBarChart size={28} strokeWidth={2} />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black text-slate-900 tracking-tight">Project Reports</h3>
                                    <p className="text-sm font-semibold text-slate-500 mt-0.5">Generate and manage compliance status reports</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    className="flex gap-2 items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-md active:scale-95"
                                    onClick={generateReport} disabled={isGenerating}>
                                    {isGenerating ? (
                                        <><Loader2 size={18} className="animate-spin text-indigo-400" /> Generating...</>
                                    ) : (
                                        <><Blocks size={18}  /> Generate New Report</>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* --- REPORTS LIST OR EMPTY STATE --- */}
                        <div className="relative z-10">
                            {reports && reports.length > 0 ? (
                                <div className="grid grid-cols-1 gap-4">
                                    {reports.map((report, idx) => (
                                        <div key={report.reportId || idx} className="animate-in fade-in slide-in-from-bottom-4" style={{ animationDelay: `${idx * 50}ms` }}>
                                            <ReportsItem
                                                setPageIsLoading={setPageIsLoading}
                                                pageIsLoading={pageIsLoading}
                                                report={report}
                                                currentUser={currentUser}
                                                manageViewButton={manageViewButton}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-20 bg-slate-50/50 rounded-3xl border-2 border-dashed border-slate-200">
                                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5 shadow-sm ring-1 ring-slate-200">
                                        <FileBarChart className="h-10 w-10 text-indigo-300" />
                                    </div>
                                    <h3 className="text-xl font-black text-slate-900 mb-2">No Reports Generated</h3>
                                    <p className="text-sm font-medium text-slate-500 mb-8 max-w-sm mx-auto">
                                        You haven't generated any compliance reports for this project yet. Click the button below to create your first one.
                                    </p>
                                    <button
                                        onClick={generateReport}
                                        disabled={isGenerating}
                                        className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold text-indigo-700 bg-indigo-50 ring-1 ring-indigo-200/50 rounded-xl hover:bg-indigo-600 hover:text-white transition-colors"
                                    >
                                        {isGenerating ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                                        Generate First Report
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Document Preview Modal */}
                        <FilePreviewModal
                            setPageIsLoading={setPageIsLoading}
                            pageIsLoading={pageIsLoading}
                            isOpen={isEvidenceModalOpen}
                            onClose={() => setIsEvidenceModalOpen(false)}
                            file={{ fileType: "pdf", fileUrl: generatedReports?.fileUrl, name: generatedReports?.reportName || "Generated Report" }}
                        />
                    </div>
                </div >
            </Spin>
        </Content>
    );
};

export default ReportsView;